import { Box, Grid, Paper, Toolbar, Tabs, Tab } from '@mui/material';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { getOrdersListWithFilter } from '@app/adapter/order-service';
import { getUserFollowList } from '@app/adapter/user-service';
import {
  OrderItem,
  OrderTableComponent,
} from '@app/components/Orders/OrderTableComponent';
import { PageTitle } from '@app/components/Shared/PageTitle';
import {
  loggedInUserState,
  snackbarOpenState,
  snackbarTextState,
} from '@app/domain/app';
import { organizationSelector } from '@app/domain/organization';
import { Order } from '@app/types/order';
import { convertOrderLocalized } from '@app/utils/order';

export function Users() {
  const [allOrders, setAllOrders] = useState<OrderItem[]>([]);
  const loggedInUser = useRecoilValue(loggedInUserState);
  const organizationState = useRecoilValue(organizationSelector);
  const setSnackbarOpen = useSetRecoilState(snackbarOpenState);
  const setSnackbarText = useSetRecoilState(snackbarTextState);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [allCount, setAllCount] = useState(0);
  const [followUserIds, setFollowUserIds] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
  });
  // データ取得に使用するタイマー（無駄な検索が走らないための対策用）
  const loadTimerRef = useRef({
    ms: 0,
    timeoutId: undefined as NodeJS.Timeout | undefined,
  });

  const loadFollowing = useCallback(async () => {
    if (!loggedInUser?.id) {
      return;
    }
    try {
      const result = await getUserFollowList(loggedInUser.id, 'user', {
        pageNumber: pagination.page,
        pageSize: pagination.pageSize,
      }); // TODO:APIが期間やキーワード検索可能になればそれに対応する
      setFollowUserIds(result.value.map((f) => f.followId));
      setAllCount(result.total);
    } catch (error) {
      setSnackbarText(
        `フォローの取得に失敗しました, ${
          error instanceof Error ? error.message : error
        }`
      );
      setSnackbarOpen(true);
    }
  }, [loggedInUser, pagination, setSnackbarOpen, setSnackbarText]);

  const getOrders = useCallback(async () => {
    if (!followUserIds.length) {
      return;
    }
    try {
      setIsLoadingOrder(true);
      const orders = [] as Order[];
      // 300件を上限にフォローユーザーの応募を取得する
      for (let i = 0; i < 3; i++) {
        const userIds = followUserIds.filter(
          (id) => !orders.some((o) => o.customer.user?.id === id)
        );
        if (!userIds.length) {
          break;
        }
        const result = await getOrdersListWithFilter(organizationState.id, {
          order: 'customFields.productDay desc',
          pageNumber: 0,
          pageSize: 100,
          userIds,
        });
        result.data.value.forEach((o) => orders.push(o));
      }
      const orderList = followUserIds.map((userId) => {
        const order = orders.find(
          (o) =>
            o.customer.user?.id === userId &&
            ['ACCEPTED', 'CLOSED', 'PROCESSING'].includes(o.status)
        );
        if (order) {
          return convertOrderLocalized(order);
        }
        const order2 = orders.find((o) => o.customer.user?.id === userId);
        if (order2) {
          return convertOrderLocalized(order2);
        }
        return { customer: { user: { id: userId } } } as OrderItem;
      });
      setAllOrders(orderList);
    } catch (error: unknown) {
      setSnackbarText(
        `応募の取得に失敗しました, ${
          error instanceof Error ? error.message : error
        }`
      );
      setSnackbarOpen(true);
    } finally {
      setIsLoadingOrder(false);
    }
  }, [followUserIds, organizationState, setSnackbarOpen, setSnackbarText]);

  useEffect(() => {
    // NOTE:無駄な検索防止に一定時間検索待機
    clearTimeout(loadTimerRef.current.timeoutId);
    const timeoutId = setTimeout(() => {
      void loadFollowing();
    }, loadTimerRef.current.ms);
    loadTimerRef.current = { ms: 0, timeoutId };
  }, [loadFollowing]);

  useEffect(() => {
    void getOrders();
  }, [getOrders]);

  return (
    <>
      <Box sx={{ mb: 3, mt: 1 }}>
        <Box alignItems="baseline" display="flex" gap={5}>
          <PageTitle title="医師" data-e2e="product-title" />
        </Box>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={0}>
                <Tab label="フォロー中" value={0} />
              </Tabs>
            </Box>
          </Grid>
        </Grid>

        <Toolbar sx={{ px: '1rem !important' }}></Toolbar>

        <OrderTableComponent
          mode="user"
          orders={allOrders}
          rowCount={allCount}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          loading={isLoadingOrder}
        />
      </Paper>
    </>
  );
}
